import produce, { Draft } from 'immer';

import { Dictionary } from 'lodash';
import {
  IAddByocCollection,
  IAddByocTile,
  IDeleteByocCollection,
  IDeleteByocTile,
  ISetByocCollection,
  ISetByocCollectionFetching,
  ISetByocCollectionFetchingFailed,
  ISetUserByocCollections,
  ISetByocCollectionSearchQuery,
  ISetUserByocCollectionsFetching,
  ISetUserByocCollectionsFetchingFailed,
  ISetByocCollectionTiles,
  ISetByocCollectionTilesFetching,
  IUpdateByocCollection,
  IUpdateByocTile,
  IAddByocCollections,
} from '../../actions/byoc';
import * as actionTypes from '../../constants/actionTypes';
import {
  ByocCollection,
  ByocCollectionsCache,
  ByocTilesCache,
  CollectionCacheRecord,
  FetchedState,
} from '../../meta/types/byoc';

export const initialState = Object.freeze({
  fetchingCollections: false,
  fetched: false,
  collectionsCache: {} as ByocCollectionsCache,
  collectionSearchQuery: '',
  tileCache: {} as ByocTilesCache,
});

export type ByocState = typeof initialState & {
  collectionsPromise?: Promise<ByocCollection[]>;
  collectionFetchingFailed?: boolean;
};

type ByocAction =
  | ISetUserByocCollectionsFetching
  | ISetUserByocCollectionsFetchingFailed
  | ISetUserByocCollections
  | ISetByocCollectionFetching
  | ISetByocCollectionFetchingFailed
  | ISetByocCollection
  | IDeleteByocCollection
  | IUpdateByocCollection
  | ISetByocCollectionSearchQuery
  | ISetByocCollectionTiles
  | ISetByocCollectionTilesFetching
  | IAddByocTile
  | IUpdateByocTile
  | IDeleteByocTile
  | IAddByocCollection
  | IAddByocCollections;

const byoc = (state: ByocState = initialState, action: ByocAction) => {
  return produce<ByocState>(state, (draft: Draft<ByocState>) => {
    switch (action.type) {
      case actionTypes.UPDATE_BYOC_TILE:
        draft.tileCache[action.collectionId] = {
          ...draft.tileCache[action.collectionId],
          tiles: draft.tileCache[action.collectionId].tiles.map(t =>
            t.id === action.tile.id ? action.tile : t,
          ),
        };
        return;
      case actionTypes.DELETE_BYOC_TILE:
        draft.tileCache[action.collectionId] = {
          ...draft.tileCache[action.collectionId],
          tiles: draft.tileCache[action.collectionId].tiles.filter(t => t.id !== action.tileId),
        };
        return;
      case actionTypes.ADD_BYOC_TILE:
        draft.tileCache[action.collectionId] = {
          ...draft.tileCache[action.collectionId],
          tiles: [action.tile, ...draft.tileCache[action.collectionId].tiles],
        };
        return;
      case actionTypes.SET_BYOC_COLLECTION_TILES_FETCHING:
        draft.tileCache[action.collectionId] = {
          fetching: true,
          query: action.query,
          tilesPromise: action.tilesPromise,
          tiles: [],
          links: {},
        };
        return;
      case actionTypes.SET_BYOC_COLLECTION_TILES:
        draft.tileCache[action.collectionId] = {
          fetching: false,
          tilesPromise: undefined,
          tiles: action.tiles,
          links: action.links,
        };
        return;
      case actionTypes.SET_BYOC_SEARCH_QUERY:
        draft.collectionSearchQuery = action.searchQuery;
        return;
      case actionTypes.UPDATE_BYOC_COLLECTION:
        draft.collectionsCache[action.collection.id] = {
          ...state.collectionsCache[action.collection.id],
          collection: action.collection,
        };
        return;
      case actionTypes.ADD_BYOC_COLLECTION:
        draft.collectionsCache[action.collection.id] = {
          collection: action.collection,
          fetchedState: FetchedState.FETCHED,
        };
        return;
      case actionTypes.DELETE_BYOC_COLLECTION:
        delete draft.collectionsCache[action.collectionId];
        return;
      case actionTypes.SET_USER_BYOC_COLLECTIONS_FETCHING:
        draft.fetchingCollections = true;
        draft.collectionFetchingFailed = false;
        draft.collectionsPromise = action.collectionsPromise;
        return;
      case actionTypes.SET_USER_BYOC_COLLECTIONS_FETCHING_FAILED:
        draft.fetchingCollections = false;
        draft.collectionsPromise = undefined;
        draft.fetched = false;
        draft.collectionFetchingFailed = true;
        return;
      case actionTypes.SET_USER_BYOC_COLLECTIONS_FETCHED:
        draft.fetchingCollections = false;
        draft.collectionsPromise = undefined;
        draft.fetched = true;
        return;
      case actionTypes.ADD_BYOC_COLLECTIONS:
        draft.collectionsCache = {
          ...draft.collectionsCache,
          ...action.collections.reduce((map, obj) => {
            map[obj.id] = {
              collection: obj,
              fetchedState: FetchedState.NOT_FETCHED,
            };
            return map;
          }, {} as Dictionary<CollectionCacheRecord>),
        };
        return;
      case actionTypes.SET_BYOC_COLLECTION_FETCHING:
        draft.collectionsCache[action.collectionId] = {
          ...draft.collectionsCache[action.collectionId],
          fetchedState: FetchedState.FETCHING,
        };
        return;
      case actionTypes.SET_BYOC_COLLECTION_FETCHING_FAILED:
        draft.collectionsCache[action.collectionId] = {
          ...state.collectionsCache[action.collectionId],
          fetchedState: FetchedState.FETCHING_FAILED,
        };
        return;
      case actionTypes.SET_BYOC_COLLECTION:
        draft.collectionsCache[action.collection.id] = {
          collection: {
            ...state.collectionsCache[action.collection.id].collection,
            ...action.collection,
          },
          fetchedState: FetchedState.FETCHED,
        };
        return;
      default:
        return;
    }
  });
};

export default byoc;
